import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaBars,
  FaChevronDown,
  FaShieldAlt,
} from "react-icons/fa";
import {
  User,
  LogOut,
  Download,
  Pill,
  FlaskConical,
  Activity,
  Package,
  Sparkles,
  PhoneCall,
  Home,
  Building2,
} from "lucide-react";

import SearchBar from "./SearchBar";
import SignInButton from "./SignInButton";
import LocationPopup from "../common/LocationPopup";
import LogoutPopup from "../../component/common/LogoutPopup";
import MobileSidebar from "./MobileSidebar";

import logo from "../assets/images/medrpha_logo.png";
import cartIcon from "../assets/images/add to cart.png";

import useNavbarAPI from "../../services/Navbar/NavbarAPI";

export default function Navbar({ onCartClick }) {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    totalItems,
    address,
    loading,
    fetchLocation,
    displayName,
    openCart,
    confirmLogout,
  } = useNavbarAPI();

  const handleLogoutClick = () => {
    setShowLogoutPopup(true);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setShowProfileDropdown(false);
  };

  // Click outside handler
  React.useEffect(() => {
    const handleClickOutside = () => {
      setShowProfileDropdown(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const navItems = [
    { name: "Home", route: "/home", icon: <Home size={15} /> },
    { name: "Lab Tests", route: "/all-tests", from: "labtests", icon: <FlaskConical size={15} /> },
    { name: "Health Packages", route: "/all-packages", from: "all-packages", icon: <Package size={15} /> },
    { name: "Medicines", route: "/medicine", icon: <Pill size={15} /> },
    { name: "Find Labs", route: "/lab", from: "lab", icon: <Building2 size={15} /> },
    { name: "Health Campaign", route: "/", icon: <Sparkles size={15} className="text-amber-500" /> },
    { name: "Partner With Us", route: "/partner-with-us", icon: <User size={15} /> },
  ];

  return (
    <>
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-xs border-b border-slate-100 z-50 transition-all duration-300">
        {/* TOP UTILITY STRIP - LIGHT THEME */}
        <div className="hidden lg:flex bg-slate-50 text-slate-600 text-[11px] px-8 py-1.5 justify-between items-center border-b border-slate-200/70">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-emerald-700 font-bold">
              <FaShieldAlt size={11} className="text-emerald-600" /> 100% NABL & CAP Accredited Partner Labs
            </span>
            <span className="text-slate-300">|</span>
            <span className="font-medium text-slate-600">Free Home Sample Collection in 60 Mins</span>
            <span className="text-slate-300">|</span>
            <span className="font-medium text-slate-600">Verified Digital Reports in 6 Hours</span>
          </div>
          <div className="flex items-center gap-4 font-semibold">
            <a href="tel:8178399885" className="hover:text-red-600 flex items-center gap-1 text-slate-700 transition-colors">
              <PhoneCall size={11} className="text-red-600" /> Hotline: +91 81783 99885
            </a>
            <span className="text-slate-300">|</span>
            <span className="text-red-600 font-bold">⚡ Fast Diagnostic Speed Guarantee</span>
          </div>
        </div>

        {/* MAIN NAV HEADER */}
        <div className="h-16 md:h-[72px] px-4 md:px-6 lg:px-8 flex items-center justify-between gap-3 md:gap-6">
          
          {/* LEFT SECTION */}
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50 text-slate-700 transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                setShowMobileMenu(true);
              }}
              aria-label="Open menu"
            >
              <FaBars size={18} />
            </button>

            <Link to="/" className="flex-shrink-0 flex items-center gap-2">
              <img src={logo} alt="Medrpha Labs" className="h-8 md:h-10 object-contain" />
            </Link>

            {/* LOCATION PICKER - DESKTOP */}
            <div 
              onClick={() => setOpenPopup(true)}
              className="hidden xl:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-red-50/60 border border-slate-200/80 cursor-pointer transition-all duration-200 group ml-2 hover:border-red-300"
            >
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 flex-shrink-0">
                <FaMapMarkerAlt size={11} />
              </div>
              <div className="flex flex-col text-left">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Deliver to</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-slate-800 truncate max-w-[140px]">
                    {loading ? "Detecting..." : address || "Select Location"}
                  </span>
                  <FaChevronDown
                    className="text-slate-400 text-[10px] group-hover:text-red-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SEARCH BAR - DESKTOP */}
          <div className="hidden lg:block flex-1 max-w-2xl mx-2">
            <SearchBar />
          </div>

          {/* RIGHT SECTION */}
          <div className="flex items-center gap-3 md:gap-4">
            
            {/* LOCATION PICKER - TABLET */}
            <div 
              onClick={() => setOpenPopup(true)}
              className="hidden md:flex xl:hidden items-center gap-1.5 text-slate-700 cursor-pointer bg-slate-50 hover:bg-red-50/60 px-2.5 py-1.5 rounded-full border border-slate-200 text-xs font-semibold hover:border-red-300 transition-all"
            >
              <FaMapMarkerAlt className="text-red-500" size={13} />
              <span className="truncate max-w-[100px]">
                {loading ? "..." : address || "Location"}
              </span>
              <FaChevronDown 
                className="text-slate-400 text-[10px]" 
              />
            </div>

            {/* DOWNLOAD APP BUTTON - DIRECT PLAY STORE */}
            <a
              href="https://play.google.com/store/apps/details?id=com.medrpha_labs.app"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-red-200 text-red-600 bg-red-50/70 text-xs font-bold hover:bg-red-600 hover:text-white transition-all duration-200 shadow-2xs cursor-pointer"
              title="Download Medrph Labs on Google Play"
            >
              <Download size={14} className="animate-bounce" />
              <span>Get App</span>
            </a>

            {/* CART ICON */}
            <button
              className="relative p-2.5 hover:bg-slate-100 rounded-full transition-all duration-200 cursor-pointer border border-transparent hover:border-slate-200"
              onClick={(e) => {
                e.stopPropagation();
                openCart();
              }}
              aria-label="View cart"
            >
              <img src={cartIcon} alt="cart" className="w-5 h-5 object-contain" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] min-w-[20px] h-[20px] px-1 flex items-center justify-center rounded-full font-bold shadow-sm animate-pulse">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </button>

            {/* USER PROFILE */}
            {user ? (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowProfileDropdown((prev) => !prev)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-red-500 hover:bg-red-50/30 transition-all duration-200 shadow-2xs"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-red-600 to-rose-500 flex items-center justify-center text-xs font-bold text-white shadow-2xs flex-shrink-0">
                    {displayName?.charAt(0)}
                  </div>
                  <span className="text-xs font-bold text-slate-800 hidden sm:inline-block max-w-[100px] truncate">
                    {displayName}
                  </span>
                  <FaChevronDown className="text-slate-400 text-[10px]" />
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-3 w-56 bg-white shadow-xl rounded-2xl py-2 z-50 border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
                    <div className="px-4 pb-3 mb-2 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold text-base border border-red-100">
                          {displayName?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-sm text-slate-900 truncate">
                            {displayName}
                          </h4>
                          <span className="text-[11px] text-emerald-600 font-medium">✓ Verified Account</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleNavigate("/profile")}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors duration-200"
                    >
                      <User size={15} className="text-slate-400" />
                      <span>My Profile & Orders</span>
                    </button>

                    <div className="px-4 pt-2 pb-1">
                      <button
                        onClick={handleLogoutClick}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-xl font-bold transition-all duration-200 border border-red-100 hover:border-red-600"
                      >
                        <LogOut size={14} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="scale-[0.85] sm:scale-[0.92] md:scale-100">
                <SignInButton />
              </div>
            )}
          </div>
        </div>

        {/* MOBILE SEARCH BAR */}
        <div className="lg:hidden px-4 pb-3 bg-white border-t border-slate-100">
          <SearchBar />
        </div>
      </nav>

      {/* DESKTOP SECONDARY NAVIGATION - APOLLO QUICK SERVICES */}
      <div className="hidden lg:flex fixed top-[100px] w-full bg-white border-b border-slate-200/90 min-h-[54px] py-2 px-6 items-center justify-center z-40 shadow-2xs">
        <div className="flex items-center justify-center gap-2.5 max-w-7xl mx-auto flex-wrap">
          {navItems.map((item, index) => {
            const isActive =
              location.pathname === item.route &&
              (!item.from || location.state?.from === item.from);
            return (
              <button
                key={index}
                onClick={() =>
                  navigate(item.route, {
                    state: { from: item.from, name: item.name },
                  })
                }
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  isActive
                    ? "bg-red-600 text-white shadow-xs shadow-red-200"
                    : "text-slate-700 bg-slate-50 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/70"
                }`}
              >
                {item.icon}
                <span className="leading-normal">{item.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* MOBILE SIDEBAR */}
      <MobileSidebar
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        navItems={navItems}
        location={location}
        navigate={navigate}
        user={user}
        displayName={displayName}
        handleLogoutClick={handleLogoutClick}
        logo={logo}
        address={address}
        onLocationClick={() => {
          setShowMobileMenu(false);
          setOpenPopup(true);
        }}
      />

      {/* POPUPS */}
      <LogoutPopup
        isOpen={showLogoutPopup}
        onCancel={() => setShowLogoutPopup(false)}
        onConfirm={confirmLogout}
      />
      <LocationPopup isOpen={openPopup} onClose={() => setOpenPopup(false)} />
    </>
  );
}